import * as React from "react"
import * as ProgressPrimitive from "@radix-ui/react-progress"
import { cn } from "@/lib/utils"

const clampProgressValue = (value) => {
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return 0
  return Math.max(0, Math.min(100, numericValue))
}

const Progress = React.forwardRef(({ className, value, ...props }, ref) => {
  const progressValue = clampProgressValue(value)

  return (
    <ProgressPrimitive.Root
      ref={ref}
      className={cn(
        "relative h-2 w-full overflow-hidden rounded-full bg-slate-100",
        className
      )}
      value={progressValue}
      max={100}
      {...props}
    >
      <ProgressPrimitive.Indicator
        className="h-full w-full flex-1 bg-blue-600 transition-all"
        style={{ transform: `translateX(-${100 - progressValue}%)` }}
      />
    </ProgressPrimitive.Root>
  )
})
Progress.displayName = ProgressPrimitive.Root.displayName

// Fitness-specific progress variants
const FitnessProgress = React.forwardRef(({ 
  className, 
  value, 
  variant = "default",
  showPercentage = false,
  label,
  ...props 
}, ref) => {
  const progressValue = clampProgressValue(value)

  const getVariantStyles = () => {
    switch (variant) {
      case "calories":
        return "bg-orange-500"
      case "protein":
        return "bg-blue-600"
      case "carbs":
        return "bg-emerald-500"
      case "fat":
        return "bg-amber-500"
      case "water":
        return "bg-sky-500"
      case "workout":
        return "bg-blue-600"
      default:
        return "bg-blue-600"
    }
  }

  return (
    <div className="space-y-2">
      {label && (
        <div className="flex justify-between text-sm">
          <span className="font-medium">{label}</span>
          {showPercentage && (
            <span className="text-muted-foreground">{Math.round(progressValue)}%</span>
          )}
        </div>
      )}
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(
          "relative h-2 w-full overflow-hidden rounded-full bg-slate-100",
          className
        )}
        value={progressValue}
        max={100}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(
            "h-full w-full flex-1 transition-all duration-500 ease-out",
            getVariantStyles()
          )}
          style={{ transform: `translateX(-${100 - progressValue}%)` }}
        />
      </ProgressPrimitive.Root>
    </div>
  )
})
FitnessProgress.displayName = "FitnessProgress"

// Circular progress for fitness metrics
const CircularProgress = React.forwardRef(({ 
  value = 0, 
  size = 120, 
  strokeWidth = 8,
  className,
  children,
  variant = "default",
  ...props 
}, ref) => {
  const progressValue = clampProgressValue(value)
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const strokeDasharray = circumference
  const strokeDashoffset = circumference - (progressValue / 100) * circumference

  const getVariantColor = () => {
    switch (variant) {
      case "calories": return "#F97316"
      case "protein": return "#2563EB"
      case "carbs": return "#10B981"
      case "fat": return "#F59E0B"
      case "water": return "#06B6D4"
      case "workout": return "#2563EB"
      default: return "#2563EB"
    }
  }

  return (
    <div className={cn("relative inline-flex items-center justify-center", className)} ref={ref}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
        {...props}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="currentColor"
          strokeWidth={strokeWidth}
          fill="none"
          className="text-slate-100"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={getVariantColor()}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-500 ease-out"
        />
      </svg>
      {/* Content in center */}
      <div className="absolute inset-0 flex items-center justify-center">
        {children}
      </div>
    </div>
  )
})
CircularProgress.displayName = "CircularProgress"

export { Progress, FitnessProgress, CircularProgress }